import {
  AwardIcon,
  LockIcon,
  RadioGroupItemBase,
  Table,
  Text,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { ChooseCapabilityPopover } from './ChooseCapabilityPopover'
import type { LinkedInVerifiedCapability } from './config'
import styles from './LinkedInVerifiedSkills.module.css'

type SkillGroupProps = {
  label: string
  capabilities: LinkedInVerifiedCapability[]
  isEarned: boolean
  isSelectionDisabled: boolean
  shouldShowSelectionColumn: boolean
  chooseCapabilityPopoverCapabilityKey?: LinkedInVerifiedCapability['capabilityKey']
  isChooseCapabilityPopoverOpen: boolean
  shouldShowChooseCapabilityPopover: boolean
  onChooseCapabilityPopoverOpenChange: (isOpen: boolean) => void
}

const SkillGroup = ({
  label,
  capabilities,
  isEarned,
  isSelectionDisabled,
  shouldShowSelectionColumn,
  chooseCapabilityPopoverCapabilityKey,
  isChooseCapabilityPopoverOpen,
  shouldShowChooseCapabilityPopover,
  onChooseCapabilityPopoverOpenChange,
}: SkillGroupProps): React.JSX.Element => {
  const textColor = isEarned ? undefined : 'subtle'
  const Icon = isEarned ? AwardIcon : LockIcon
  const iconLabel = isEarned ? 'Achieved capability' : 'Capability not achieved'
  const iconTestId = isEarned
    ? 'linkedin-capability-earned-icon'
    : 'linkedin-capability-locked-icon'

  return (
    <>
      <Table.Row className={styles.groupLabelRow}>
        <Table.Cell colSpan={shouldShowSelectionColumn ? 3 : 2}>
          <Text size="sm" weight="medium">
            {label}
          </Text>
        </Table.Cell>
      </Table.Row>
      {capabilities.map((capability) => (
        <Table.Row key={capability.capabilityKey}>
          <Table.Cell>
            <span className={styles.skillCell}>
              <Icon
                size="small"
                color={isEarned ? 'brand' : undefined}
                className={isEarned ? undefined : styles.notEarnedIcon}
                data-testid={iconTestId}
              />
              <VisuallyHidden>{iconLabel}: </VisuallyHidden>
              <Text weight="medium" color={textColor}>
                {capability.name}
              </Text>
            </span>
          </Table.Cell>
          <Table.Cell>
            <Text color={textColor}>{capability.description}</Text>
          </Table.Cell>
          {shouldShowSelectionColumn ? (
            <Table.Cell align="center" className={styles.showCell}>
              {isEarned ? (
                <ChooseCapabilityPopover
                  isEnabled={
                    shouldShowChooseCapabilityPopover &&
                    chooseCapabilityPopoverCapabilityKey ===
                      capability.capabilityKey
                  }
                  isOpen={
                    isChooseCapabilityPopoverOpen &&
                    chooseCapabilityPopoverCapabilityKey ===
                      capability.capabilityKey
                  }
                  onOpenChange={onChooseCapabilityPopoverOpenChange}
                >
                  <RadioGroupItemBase
                    aria-label={`Show ${capability.name} on LinkedIn`}
                    disabled={isSelectionDisabled}
                    value={capability.capabilityKey}
                  />
                </ChooseCapabilityPopover>
              ) : null}
            </Table.Cell>
          ) : null}
        </Table.Row>
      ))}
    </>
  )
}

export { SkillGroup }
